import { sendMetrics } from '@jusbrasil-web/shared-utils-events'

import { resolveSignupModalCopy } from '../../experiments/signup-modal/copy'
import { SIGNUP_MODAL_EVENTS } from '../../experiments/signup-modal/events'
import {
  type SignupModalVariant,
  SIGNUP_MODAL_FLAG_KEY,
  SIGNUP_MODAL_METRICS_API_ROUTE,
} from '../../experiments/signup-modal/flag'
import type { SignupModalSegment } from '../../experiments/signup-modal/segment'
import { useFireOnce } from '../../hooks/useFireOnce'
import { useSentryExperimentTag } from '../../hooks/useSentryExperimentTag'
import { buildUserAccountEventData } from '../../utils/event-data'

export type SignupModalCtaType = 'signup' | 'login'

export interface SignupModalHeaderProps {
  variant: SignupModalVariant | null
  segment: SignupModalSegment
  entityName?: string
  entityType?: 'PF' | 'CNPJ'
  appId?: string
  featureCode?: string
  fromComponent?: string
  inExperiment?: boolean
}

export type SignupModalCtaTrackerProps = Pick<
  SignupModalHeaderProps,
  'segment' | 'appId' | 'featureCode' | 'fromComponent' | 'inExperiment'
> & {
  variant: SignupModalVariant | null | undefined
}

function beaconSignupModalHealth(body: Record<string, unknown>) {
  if (typeof fetch !== 'function') return
  fetch(SIGNUP_MODAL_METRICS_API_ROUTE, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    keepalive: true,
  }).catch(() => undefined)
}

export function useSignupModalCtaTracker(props: SignupModalCtaTrackerProps) {
  return (ctaType: SignupModalCtaType) => {
    if (!props.variant) return
    const payload = buildSignupModalPayload({ ...props, variant: props.variant })
    sendMetrics(SIGNUP_MODAL_EVENTS.CTA_CLICKED, null, { ...payload, cta_type: ctaType })
    beaconSignupModalHealth({
      event: 'cta',
      ctaType,
      variant: props.variant,
      segment: props.segment,
      appId: props.appId ?? '',
      inExperiment: props.inExperiment ?? false,
    })
  }
}

function buildSignupModalPayload(props: {
  variant: SignupModalVariant
  segment: SignupModalSegment
  appId?: string
  featureCode?: string
  fromComponent?: string
}): Record<string, string> {
  const base = buildUserAccountEventData(
    {
      appId: props.appId,
      featureCode: props.featureCode,
      fromComponent: props.fromComponent,
    },
    { type: 'LogInModal' }
  )
  return {
    ...base,
    variant: props.variant,
    segment: props.segment,
  }
}

export function SignupModalHeader({
  variant,
  segment,
  entityName,
  entityType,
  appId,
  featureCode,
  fromComponent,
  inExperiment = false,
}: SignupModalHeaderProps) {
  useSentryExperimentTag(SIGNUP_MODAL_FLAG_KEY, variant)

  const renderVariant: SignupModalVariant = variant ?? 'control'
  const { sentinel } = resolveSignupModalCopy({
    variant: renderVariant,
    segment,
    entityName,
    entityType,
  })

  // Mount-only by design: post-hydration variant changes are not re-checked here.
  useFireOnce(() => {
    const payload = buildSignupModalPayload({
      variant: renderVariant,
      segment,
      appId,
      featureCode,
      fromComponent,
    })
    if (sentinel !== null) {
      sendMetrics(SIGNUP_MODAL_EVENTS.FALLBACK_USED, null, { ...payload, sentinel })
    }
    sendMetrics(SIGNUP_MODAL_EVENTS.RENDERED, null, payload)
    // One render beacon per mount; outcome encodes whether a fallback name was
    // used (sentinel != null) so signup_modal_render_total isn't double-counted.
    beaconSignupModalHealth({
      event: 'render',
      outcome: sentinel !== null ? 'fallback_used' : 'rendered',
      variant: renderVariant,
      segment,
      appId: appId ?? '',
      inExperiment,
    })
  })

  return null
}
